import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import Popup from "src/components/Popup/Popup";
import { useApi } from "src/hooks/useApi";
import { TbChartPie, TbTimeline, TbListNumbers } from "react-icons/tb";
import type { Invoice } from "src/types";

interface TopService { code: string; description: string; count: number; totalAmount: number; }
interface YearMonthStat { label: string; sent: number; paid: number; }

interface Props {
    invoices: Invoice[];
    pieSent: number;
    amountM1: number;
    amountM2: number;
    amountM3: number;
    amountLegal: number;
    yearMonthlyData: YearMonthStat[];
    onClose: () => void;
}

function fmt(n: number) {
    return n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtShort(n: number) {
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 }) + " Mio. €";
    if (Math.abs(n) >= 10_000) return (n / 1_000).toLocaleString("de-DE", { maximumFractionDigits: 1 }) + " k €";
    return fmt(n);
}

// ── Tab 1: Invoice summary donut — the pie chart this popup replaced, unchanged in
// substance (same slices/colors/toggle-to-hide legend), just re-hosted as a tab. ──────

const PIE_SLICES = [
    { key: "sent",  color: "#6366f1" },
    { key: "m1",    color: "#22c55e" },
    { key: "m2",    color: "#f97316" },
    { key: "m3",    color: "#ef4444" },
    { key: "legal", color: "#6b7280" },
] as const;
type PieSliceKey = typeof PIE_SLICES[number]["key"];

const PIE_R = 80, PIE_CX = 110, PIE_CY = 110, PIE_CIRC = 2 * Math.PI * PIE_R, PIE_GAP = 2;

function SummaryPieTab({ pieSent, amountM1, amountM2, amountM3, amountLegal }: { pieSent: number; amountM1: number; amountM2: number; amountM3: number; amountLegal: number }) {
    const { t } = useTranslation();
    const [hidden, setHidden] = useState<Set<PieSliceKey>>(new Set());

    const allValues: Record<PieSliceKey, number> = { sent: pieSent, m1: amountM1, m2: amountM2, m3: amountM3, legal: amountLegal };
    const toggle = (key: PieSliceKey) => setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const activeValues = PIE_SLICES.map((s) => (hidden.has(s.key) ? 0 : allValues[s.key]));
    const total = activeValues.reduce((s, v) => s + v, 0);

    const segments = useMemo(() => {
        const results: { dash: number; offset: number }[] = [];
        let cumulative = 0;
        for (const v of activeValues) {
            const arc = total > 0 ? (v / total) * PIE_CIRC : 0;
            const visibleDash = Math.max(0, arc - PIE_GAP);
            results.push({ dash: visibleDash, offset: -cumulative });
            cumulative += arc;
        }
        return results;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(activeValues), total]);

    return (
        <div className="summary-pie">
            <svg viewBox="0 0 220 220" className="summary-pie__svg">
                <g transform={`rotate(-90, ${PIE_CX}, ${PIE_CY})`}>
                    {total <= 0 ? (
                        <circle cx={PIE_CX} cy={PIE_CY} r={PIE_R} fill="none" stroke="#f3f4f6" strokeWidth={28} />
                    ) : (
                        PIE_SLICES.map((s, i) => (
                            <circle
                                key={s.key}
                                className="summary-pie__arc"
                                cx={PIE_CX} cy={PIE_CY} r={PIE_R}
                                fill="none" stroke={s.color} strokeWidth={28}
                                strokeDasharray={`${segments[i].dash} ${PIE_CIRC - segments[i].dash}`}
                                strokeDashoffset={segments[i].offset}
                            />
                        ))
                    )}
                </g>
                <text x={PIE_CX} y={PIE_CY - 10} textAnchor="middle" fontSize={10} fill="#9ca3af" fontWeight={500}>
                    {t("dashboard.summaryPie.total")}
                </text>
                <text x={PIE_CX} y={PIE_CY + 10} textAnchor="middle" fontSize={14} fill="#111827" fontWeight={700}>
                    {fmtShort(total)}
                </text>
            </svg>

            <div className="summary-pie__legend">
                {PIE_SLICES.map((s, i) => {
                    const isHidden = hidden.has(s.key);
                    const pct = !isHidden && total > 0 ? Math.round((allValues[s.key] / total) * 100) : 0;
                    return (
                        <div
                            key={s.key}
                            className={`summary-pie__row${isHidden ? " summary-pie__row--hidden" : ""}`}
                            style={{ "--i": i } as CSSProperties}
                            onClick={() => toggle(s.key)}
                        >
                            <span className="summary-pie__dot" style={{ background: s.color }} />
                            <div className="summary-pie__row-body">
                                <div className="summary-pie__row-top">
                                    <span className="summary-pie__label">{t(`dashboard.summaryPie.${s.key}`)}</span>
                                    <span className="summary-pie__pct">{pct}%</span>
                                </div>
                                <span className="summary-pie__value">{fmt(allValues[s.key])}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Tab 2: Month-to-month line graphs ────────────────────────────────────────────

interface LineSeries { key: string; label: string; color: string; values: number[]; }

/** `upTo`: index of the last month that has happened — later months are drawn without a line,
 *  not as a drop to 0. The legend shows the year's total so far. */
function LineChartPanel({ title, labels, series: allSeries, formatValue, upTo, index = 0 }: { title: string; labels: string[]; series: LineSeries[]; formatValue: (n: number) => string; upTo: number; index?: number }) {
    const W = 620, H = 150, PAD_X = 8, PAD_Y = 16;
    const series = allSeries.map((s) => ({ ...s, values: s.values.slice(0, upTo + 1) }));
    const maxVal = Math.max(1, ...series.flatMap((s) => s.values));
    const stepX = labels.length > 1 ? (W - PAD_X * 2) / (labels.length - 1) : 0;
    const toX = (i: number) => PAD_X + i * stepX;
    const toY = (v: number) => H - PAD_Y - (v / maxVal) * (H - PAD_Y * 2);

    return (
        <div className="dstats-linechart" style={{ "--i": index } as CSSProperties}>
            <div className="dstats-linechart__title">{title}</div>
            <svg viewBox={`0 0 ${W} ${H}`} className="dstats-linechart__svg" preserveAspectRatio="none">
                {[0, 0.5, 1].map((f) => (
                    <line key={f} x1={PAD_X} x2={W - PAD_X} y1={H - PAD_Y - f * (H - PAD_Y * 2)} y2={H - PAD_Y - f * (H - PAD_Y * 2)} stroke="#f1f5f9" strokeWidth={1} />
                ))}
                {series.map((s, si) => (
                    <polyline
                        key={s.key}
                        className="dstats-linechart__line"
                        style={{ animationDelay: `${si * 150}ms` }}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        points={s.values.map((v, i) => `${toX(i)},${toY(v)}`).join(" ")}
                    />
                ))}
                {series.map((s, si) => s.values.map((v, i) => (
                    // A visible small dot plus an invisible larger hit-area circle around it —
                    // r=2.5 alone is too small a target to reliably hover for the native title
                    // tooltip, especially once the chart is scaled down to its rendered width.
                    <g key={`${s.key}-${i}`} className="dstats-linechart__pt" style={{ animationDelay: `${450 + si * 150 + i * 30}ms` }}>
                        <circle cx={toX(i)} cy={toY(v)} r={2.5} fill={s.color} />
                        <circle cx={toX(i)} cy={toY(v)} r={9} fill="transparent">
                            <title>{`${labels[i]} · ${s.label}: ${formatValue(v)}`}</title>
                        </circle>
                    </g>
                )))}
            </svg>
            <div className="dstats-linechart__xaxis">
                {labels.map((l, i) => <span key={i}>{l}</span>)}
            </div>
            <div className="dstats-linechart__legend">
                {series.map((s) => {
                    const yearTotal = s.values.reduce((sum, v) => sum + v, 0);
                    return (
                        <span key={s.key} className="dstats-linechart__legend-item">
                            <span className="dstats-linechart__legend-dot" style={{ background: s.color }} />
                            {s.label}
                            <strong>{formatValue(yearTotal)}</strong>
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

function TrendTab({ invoices, yearMonthlyData }: { invoices: Invoice[]; yearMonthlyData: YearMonthStat[] }) {
    const { t } = useTranslation();

    const { invoicesSeries, patientsSeries } = useMemo(() => {
        const year = new Date().getFullYear();
        const invoicesPerMonth = Array.from({ length: 12 }, (_, i) =>
            invoices.filter((inv) => {
                if (!inv.creationDate) return false;
                const d = new Date(inv.creationDate);
                return d.getFullYear() === year && d.getMonth() === i;
            }).length,
        );

        // "New" patient = the earliest invoice we have for them falls in that month.
        const firstSeen = new Map<string, string>();
        for (const inv of invoices) {
            const pid = inv.patient?.id ?? inv.recipient?.id;
            if (!pid || !inv.creationDate) continue;
            const existing = firstSeen.get(pid);
            if (!existing || inv.creationDate < existing) firstSeen.set(pid, inv.creationDate);
        }
        const firstSeenDates = [...firstSeen.values()];
        const patientsPerMonth = Array.from({ length: 12 }, (_, i) =>
            firstSeenDates.filter((d) => {
                const dd = new Date(d);
                return dd.getFullYear() === year && dd.getMonth() === i;
            }).length,
        );

        return { invoicesSeries: invoicesPerMonth, patientsSeries: patientsPerMonth };
    }, [invoices]);

    const labels = yearMonthlyData.map((m) => m.label);
    // Both series run January–December of the current year.
    const currentMonth = new Date().getMonth();

    return (
        <div className="dstats-trend">
            <LineChartPanel
                index={0}
                upTo={currentMonth}
                title={t("dashboard.stats.patientsAndInvoices")}
                labels={labels}
                formatValue={(n) => String(n)}
                series={[
                    { key: "patients", label: t("dashboard.stats.newPatients"), color: "#7e22ce", values: patientsSeries },
                    { key: "invoices", label: t("dashboard.stats.invoicesCreated"), color: "#2563eb", values: invoicesSeries },
                ]}
            />
            <LineChartPanel
                index={1}
                upTo={currentMonth}
                title={t("dashboard.stats.sentAndPaid")}
                labels={labels}
                formatValue={fmtShort}
                series={[
                    { key: "sent", label: t("dashboard.sent"), color: "#f59e0b", values: yearMonthlyData.map((m) => m.sent) },
                    { key: "paid", label: t("dashboard.paid"), color: "#16a34a", values: yearMonthlyData.map((m) => m.paid) },
                ]}
            />
        </div>
    );
}

// ── Tab 3: Most popular services ─────────────────────────────────────────────────
// Two separate rankings, not one — a code billed often at a low fee can top the frequency
// list without ever earning much, and a rarely-billed expensive procedure can be the
// opposite (e.g. GOÄ code billed 10× at €1 = €10 total vs. one billed 5× at €3 = €15
// total — the second earns more despite being used less). Each row still shows both
// figures regardless of which list it's ranked in, so neither dimension is hidden.

function ServiceList({ services, rankBy }: { services: TopService[]; rankBy: "count" | "totalAmount" }) {
    const { t } = useTranslation();
    // Bars start at 0 and grow in on mount — a plain `width: X%` set on first render has
    // nothing to transition from, so the fill has to be flipped from 0 to its real width
    // one frame after mount for the CSS transition below to actually animate anything.
    const [grown, setGrown] = useState(false);
    useEffect(() => {
        const raf = requestAnimationFrame(() => setGrown(true));
        return () => cancelAnimationFrame(raf);
    }, []);

    if (services.length === 0) return <div className="dstats-empty">{t("dashboard.stats.noServices")}</div>;
    const maxVal = services[0]?.[rankBy] || 1;

    return (
        <>
            {services.map((s, i) => (
                <div className="dstats-service-row" key={s.code} style={{ "--i": i } as CSSProperties}>
                    <span className="dstats-service-row__rank">{i + 1}</span>
                    <div className="dstats-service-row__body">
                        <div className="dstats-service-row__top">
                            <span className="dstats-service-row__code">GOÄ {s.code}</span>
                            <span className="dstats-service-row__count">
                                {t("dashboard.stats.serviceCount", { count: s.count })} · {fmt(s.totalAmount)}
                            </span>
                        </div>
                        {s.description && <span className="dstats-service-row__desc">{s.description}</span>}
                        <div className="dstats-service-row__track">
                            <div className="dstats-service-row__fill" style={{ width: grown ? `${(s[rankBy] / maxVal) * 100}%` : 0 }} />
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
}

function ServicesTab() {
    const { t } = useTranslation();
    const { data, loading } = useApi<{ byCount: TopService[]; byAmount: TopService[] }>("/dashboard/top-services?limit=5");

    if (loading) return <div className="dstats-empty">{t("finapi.loading")}</div>;

    return (
        <div className="dstats-services">
            <div className="dstats-card dstats-card--flat">
                <div className="dstats-card__title">{t("dashboard.stats.mostUsed")}</div>
                <ServiceList services={data?.byCount ?? []} rankBy="count" />
            </div>
            <div className="dstats-card dstats-card--flat">
                <div className="dstats-card__title">{t("dashboard.stats.topEarning")}</div>
                <ServiceList services={data?.byAmount ?? []} rankBy="totalAmount" />
            </div>
        </div>
    );
}

// ── Popup shell with the left-side tab nav ───────────────────────────────────────

type TabKey = "summary" | "trend" | "services";

export function DashboardStatsPopup({ invoices, pieSent, amountM1, amountM2, amountM3, amountLegal, yearMonthlyData, onClose }: Props) {
    const { t } = useTranslation();
    const [tab, setTab] = useState<TabKey>("summary");

    const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
        { key: "summary", label: t("dashboard.summaryPie.title"), icon: <TbChartPie aria-hidden /> },
        { key: "trend", label: t("dashboard.stats.monthOverMonth"), icon: <TbTimeline aria-hidden /> },
        { key: "services", label: t("dashboard.stats.popularServices"), icon: <TbListNumbers aria-hidden /> },
    ];

    return (
        <Popup
            title={t("dashboard.stats.title")}
            className="popup--xl dstats-popup"
            onClose={onClose}
            actions={[{ label: t("popup.close"), variant: "primary", onClick: onClose }]}
            message={
                <div className="dstats-tabs-layout">
                    <div className="dstats-tabs-nav">
                        {tabs.map((tb) => (
                            <button
                                key={tb.key}
                                type="button"
                                className={`dstats-tabs-nav__btn${tab === tb.key ? " dstats-tabs-nav__btn--active" : ""}`}
                                onClick={() => setTab(tb.key)}
                            >
                                {tb.icon}
                                {tb.label}
                            </button>
                        ))}
                    </div>
                    <div className="dstats-tabs-content">
                        <div className="dstats-tab-panel" key={tab}>
                            {tab === "summary" && (
                                <SummaryPieTab pieSent={pieSent} amountM1={amountM1} amountM2={amountM2} amountM3={amountM3} amountLegal={amountLegal} />
                            )}
                            {tab === "trend" && <TrendTab invoices={invoices} yearMonthlyData={yearMonthlyData} />}
                            {tab === "services" && <ServicesTab />}
                        </div>
                    </div>
                </div>
            }
        />
    );
}
